import React from 'react';
import { connect } from 'react-redux';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { useConfiguration } from '../../context/configurationContext';
import { LayoutComposer, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './TransactionProcessSellerPage.module.css';

const layoutAreas = `
  topbar
  main
  footer
`;

// Shared dispute copy — identical wording is used in both the Shipping
// and Pickup sections below.
const DisputeSection = () => (
  <>
    <h3 className={css.subHeading}>Dispute</h3>
    <p className={css.paragraph}>
      If a buyer disputes an order claiming the listing doesn't correspond to
      what was described (e.g. not jumpworthy although stated, broken, etc.),
      we will evaluate and resolve the situation. If the buyer's claim is
      confirmed, the order will be cancelled, the buyer will return the gear
      to you, and you will refund the payment. We may contact the buyer's
      rigger to confirm the problem with the gear. Note that only disputes
      filed through Avento are valid, and messages sent to us directly might
      not be considered.
    </p>
    <p className={css.paragraph}>
      There is no general right to return gear for reasons other than
      material errors (e.g. the harness not fitting, although the information
      provided was correct, is not a valid reason for a return).
    </p>
    <p className={css.paragraph}>
      We try to resolve disputes as quickly as possible but within a maximum
      of 30 days. In extremely complex cases, it might take up to 50 days. If
      one party does not cooperate in resolving the issue by not responding
      for a long time or for other reasons, we may resolve the dispute in
      favour of the other party.
    </p>
  </>
);

function buildHowToSchema(rootURL) {
  return {
    '@type': 'HowTo',
    name: 'How to Sell Used Skydiving Gear on Avento',
    url: `${rootURL}/how-to-sell`,
    description:
      'Step-by-step seller process on Avento: list your gear, receive an order, ship or hand over the gear, wait for buyer inspection, and get paid out via escrow.',
    totalTime: 'P35D',
    tool: [
      { '@type': 'HowToTool', name: 'Tracked shipping carrier (DHL, UPS, FedEx, DPD, GLS, USPS, Royal Mail)' },
      { '@type': 'HowToTool', name: 'Camera for listing and packaging photos' },
    ],
    supply: [
      { '@type': 'HowToSupply', name: 'Stripe payout account' },
      { '@type': 'HowToSupply', name: 'Packaging material (box, padding)' },
    ],
    step: [
      {
        '@type': 'HowToStep',
        name: 'List your gear',
        text:
          'Create a listing with clear photos, an accurate description, and choose shipping or local pickup as the delivery option.',
      },
      {
        '@type': 'HowToStep',
        name: 'Receive an order',
        text:
          'When a buyer purchases, their payment is held in the Avento escrow account and you are notified to prepare the gear.',
      },
      {
        '@type': 'HowToStep',
        name: 'Ship within 7 days',
        text:
          'Use a reliable tracked carrier (DHL, UPS, FedEx, DPD, GLS, USPS, Royal Mail), take photos of the packaging, and mark the order as shipped on Avento. For pickup, hand the gear over within the agreed window.',
      },
      {
        '@type': 'HowToStep',
        name: 'Buyer inspection period',
        text:
          'The buyer has 28 days after shipping or 14 days after pickup to have their rigger inspect the gear and either approve the order or file a dispute.',
      },
      {
        '@type': 'HowToStep',
        name: 'Get paid out',
        text:
          'Once the order is marked as inspected or the inspection window closes without dispute, the escrow releases your payout (usually on your account within 1-2 days, max 7).',
      },
    ],
    about: {
      '@type': 'Thing',
      name: 'Selling used skydiving gear (rigs, canopies, reserves, AADs, helmets)',
    },
    inLanguage: 'en',
  };
}

function buildFaqSchema() {
  return {
    '@type': 'FAQPage',
    name: 'Avento Seller Process — Common Questions',
    description:
      'Frequently asked questions about the Avento seller process: shipping, pickup, inspection windows, disputes, and payout timelines.',
    inLanguage: 'en',
    mainEntity: [
        {
          '@type': 'Question',
          name: 'Who is responsible for choosing the shipping region?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'The buyer is responsible for choosing the correct shipping region (National, Continental, Global). If a buyer purchases an item and does not choose the right shipping region, contact the Avento team via the contact form.',
          },
        },
        {
          '@type': 'Question',
          name: 'What is the shipping time for sellers?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'After the buyer has purchased an item, you have 7 days to ship the item. Mark it as shipped on Avento. If not marked within 7 days, the order is automatically canceled.',
          },
        },
        {
          '@type': 'Question',
          name: 'Who bears the shipping risk as a seller?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'The buyer bears the shipping risk as long as the seller uses a reliable carrier (DHL, UPS, FedEx, DPD, GLS, USPS, Royal Mail), tracked shipping with dispatch confirmation, and adequate packaging. If the seller does not follow these guidelines, they may be held responsible.',
          },
        },
        {
          '@type': 'Question',
          name: 'How long does the buyer have to inspect after shipping?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'The buyer has 28 days to receive and inspect the item. If no dispute is filed, the order auto-completes and the seller is paid out.',
          },
        },
        {
          '@type': 'Question',
          name: 'What happens if the buyer disputes?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Avento evaluates the claim. If confirmed, the order is canceled, the buyer returns the gear, and the seller refunds payment. Resolution takes max 30 days (up to 50 for complex cases).',
          },
        },
        {
          '@type': 'Question',
          name: 'How long does the buyer have to pick up?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'The buyer has 14 days to pick up the item. If they don\'t pick up and the seller responded, the transaction auto-completes and the seller is paid out.',
          },
        },
      {
        '@type': 'Question',
        name: 'How long does the buyer have to inspect after pickup?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'The buyer has 14 days for rigger inspection. If marked as checked or no dispute filed within 14 days, the transaction auto-completes and the seller is paid out.',
        },
      },
    ],
  };
}

const TransactionProcessSellerPageComponent = props => {
  const { scrollingDisabled } = props;
  const config = useConfiguration();

  const rootURL = config.marketplaceRootURL || '';
  const schemaTitle = 'Selling Process on Avento';
  const schemaDescription =
    'Step-by-step guide of the selling process on Avento. Shipping times, pickup, inspection periods and payout timelines. Sell used skydiving gear quickly and with fraud protection.';
  const howToSchema = buildHowToSchema(rootURL);
  const faqSchema = buildFaqSchema();

  return (
    <Page
      title={schemaTitle}
      description={schemaDescription}
      schema={[howToSchema, faqSchema]}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {layoutProps => {
          const { Topbar, Main, Footer } = layoutProps;
          return (
            <>
              <Topbar as="header" className={css.topbar}>
                <TopbarContainer currentPage="TransactionProcessSellerPage" />
              </Topbar>

              <Main as="main" className={css.main}>
                {/* Page header */}
                <div className={css.pageHeader}>
                  <div className={css.container}>
                    <h1 className={css.pageTitle}>
                      Shipping and Pickup as a Seller
                    </h1>
                    <p className={css.pageSubtitle}>
                      The most commonly asked question to shipping and pickup.
                    </p>
                  </div>
                </div>

                {/* Shipping */}
                <section className={css.section}>
                  <div className={css.container}>
                    <h2 className={css.sectionTitle}>Shipping</h2>

                    <h3 className={css.subHeading}>Shipping Region</h3>
                    <p className={css.paragraph}>
                      The buyer is responsible to choose the correct shipping
                      region (National, Continental, Global). In case a buyer
                      purchases an item and does not choose the right shipping
                      region, please immediately contact the Avento team on the
                      contact form.
                    </p>

                    <h3 className={css.subHeading}>Shipping Time</h3>
                    <p className={css.paragraph}>
                      After the buyer has purchased an item, you have 7 days to
                      ship the item. When you shipped the item don't forget to
                      mark it as shipped on Avento! If you haven't marked the
                      item as shipped within 7 days of purchase, the order is
                      automatically canceled and the buyer will receive a full
                      refund.
                    </p>

                    <h3 className={css.subHeading}>Shipping Risk</h3>
                    <p className={css.paragraph}>
                      Generally, the buyer bears the risk of loss or damage
                      during shipping as long as you follow your obligations:
                    </p>
                    <p className={css.paragraph}>
                      You have to use a reliable carrier like DHL, UPS, FedEx,
                      DPD, GLS, USPS, or Royal Mail. You must use shipping that
                      includes online tracking and dispatch confirmation.
                      Furthermore, you should also make photos of the packaging.
                    </p>
                    <p className={css.paragraph}>
                      Only that way can we make sure that you can prove adequate
                      shipping in case of a dispute. If the buyer claims that he
                      didn't receive the Item and you don't have any tracking
                      information, the dispute will be resolved in favour of the
                      buyer.
                    </p>

                    <h3 className={css.subHeading}>Inspection</h3>
                    <p className={css.paragraph}>
                      After you shipped the item, the buyer has 28 days time to
                      receive and inspect the item. If he has not filed a
                      dispute or marked the order as inspected before that, the
                      order is automatically completed and you will be paid out.
                    </p>

                    <DisputeSection />
                  </div>
                </section>

                {/* Pickup */}
                <section className={`${css.section} ${css.lastSection}`}>
                  <div className={css.container}>
                    <h2 className={css.sectionTitle}>Pickup</h2>

                    <h3 className={css.subHeading}>Pickup Time</h3>
                    <p className={css.paragraph}>
                      After purchase, the buyer has 14 days to pick up the item
                      from you. Make sure to respond to the buyer reaching out to
                      you to arrange pickup. If the buyer does not pick up the
                      item within 14 days although you responded and didn't
                      prevent pickup, the transaction is automatically completed
                      as successful and you will be paid out.
                    </p>

                    <h3 className={css.subHeading}>Inspection</h3>
                    <p className={css.paragraph}>
                      After the buyer picks up the item, they have 14 days to
                      have it inspected by their rigger. Once they have
                      successfully inspected the item, they should mark the order
                      as checked on Avento. Then will you be paid out. If the
                      buyer doesn't mark it as checked or disputed within 14
                      days, the transaction is completed automatically, and you
                      will be paid out.
                    </p>

                    <DisputeSection />
                  </div>
                </section>
              </Main>

              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const TransactionProcessSellerPage = connect(mapStateToProps)(
  TransactionProcessSellerPageComponent
);

export default TransactionProcessSellerPage;
